import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useMetricsData } from '../../lib/admin-hooks';
import { useMetricsDateRange, generateMetricsCsv, downloadCsv } from '../../lib/metrics-hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency } from '../../lib/utils';
import { MetricsDateFilter } from '../../components/admin/MetricsDateFilter';
import { MetricsPeakInsights } from '../../components/admin/MetricsPeakInsights';
import { DailySalesChart, HourlySalesChart, ChannelPieChart, TopProductsChart } from '../../components/admin/MetricsCharts';
import { DownloadIcon, TrendingUp, TrendingDown, Loader2Icon, ChevronDown } from 'lucide-react';
import { OrdersHistoryTable } from '../../components/admin/OrdersHistoryTable';
import { CabanaCashClose } from '../../components/admin/CabanaCashClose';
import { DeliveryMsExpressSection } from '../../components/admin/DeliveryMsExpressSection';
function getDefaultDateRange(): {
  start: string;
  end: string;
} {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return {
    start: fmt(firstDay),
    end: fmt(today)
  };
}
export function MetricsPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    profitability,
    loading: metricsLoading
  } = useMetricsData(restaurant?.id);
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  const defaultRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const {
    orders,
    orderItems,
    analytics,
    loading: analyticsLoading
  } = useMetricsDateRange(restaurant?.id, startDate, endDate);
  const [isExporting, setIsExporting] = useState(false);
  // Profitability section open/closed. Defaults to OPEN so existing users
  // see the same view as before; the chevron in the header collapses it
  // when the table gets long. State is local and ephemeral by design —
  // no localStorage so each page visit starts in a predictable place.
  const [isProfitabilityOpen, setIsProfitabilityOpen] = useState(true);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-5355">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-5356" />
      </div>;
  }
  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };
  const handleExportCsv = () => {
    setIsExporting(true);
    try {
      const csv = generateMetricsCsv(analytics, orders);
      const filename = `metricas_${restaurant.slug}_${startDate}_${endDate}.csv`;
      downloadCsv(csv, filename);
    } catch (err) {
      console.error('Error exporting CSV:', err);
    } finally {
      setTimeout(() => setIsExporting(false), 600);
    }
  };
  // ─── Tenant-scoped feature: Cierre de Caja ─────────────────────────
  // Restaurantes que cobran en barra/mesa con flujo propio se benefician
  // del Cierre de Caja: La Cabaña Parrillera y NatuCafé Coffee Shop.
  // Se monta el MISMO componente para ambos (es restaurant-agnostic);
  // solo cambia el restaurantId que recibe.
  //
  // 🔒 MULTI-TENANT SAFETY: el componente recibe `restaurant.id` del
  // restaurante CARGADO desde la URL — nunca se hardcodea un id. La
  // detección es solo para decidir SI mostrar el bloque o no; los datos
  // que se muestran salen siempre del id real del tenant.
  const isCabanaParrillera = String(restaurant?.id) === '24' || restaurant?.slug === 'la-cabana-parrillera' || restaurant?.slug === 'cabana-parrillera';
  // Detección permisiva de NatuCafé: por id (19), por slug exacto, o por
  // cualquier slug que contenga "natucafe" (cubre variantes como
  // "natucafe", "natucafe-coffee-shop", "natucafe-cr", etc.).
  const isNatuCafe = String(restaurant?.id) === '19' || restaurant?.slug === 'natucafe-coffee-shop' || (restaurant?.slug?.toLowerCase().includes('natucafe') ?? false);
  // Emilius Pizza (id 27) — usa el mismo flujo de KDS / Cierre de Caja
  // que NatuCafé. Mismo componente, mismo comportamiento; solo cambia el
  // restaurantId que recibe. Emilius NO toca a NatuCafé.
  const isEmilius = String(restaurant?.id) === '27' || restaurant?.slug === 'emilius-pizza';
  const showCashClose = isCabanaParrillera || isNatuCafe || isEmilius;
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-5357">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6" data-id="element-5358">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4" data-id="element-5359">
          <div data-id="element-5360">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5361">
              Métricas y Análisis
            </h1>
            <p className="text-gray-500 text-sm mt-1" data-id="element-5362">
              Análisis detallado de ventas, tendencias y rendimiento
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={handleExportCsv} disabled={analyticsLoading || orders.length === 0} isLoading={isExporting} className="flex-shrink-0" data-id="element-5363">
            <DownloadIcon className="w-4 h-4 mr-2" data-id="element-5364" />
            Exportar CSV
          </Button>
        </div>

        {/* ─── Cierre de Caja — La Cabaña Parrillera + NatuCafé + Emilius ─── */}
        {/* Tiene su propio filtro de rango interno (mismos chips Hoy/
            Ayer/Esta Semana/etc. + Desde/Hasta), independiente del
            filtro de abajo para que cada uno cumpla su función. */}
        {showCashClose && <CabanaCashClose restaurantId={restaurant.id} data-id="element-5365" />}

        {/* Date Filter — debajo de Cierre de Caja. Controla Métricas,
            charts y la tabla de órdenes que siguen abajo. NO afecta
            al Cierre de Caja (ese tiene su propio filtro interno). */}
        <Card data-id="element-5366">
          <CardContent className="p-4" data-id="element-5367">
            <MetricsDateFilter startDate={startDate} endDate={endDate} onChange={handleDateChange} data-id="element-5368" />
          </CardContent>
        </Card>

        {/* ─── 🚚 Delivery MS Express — control de pago al repartidor ───
            Sección INDEPENDIENTE: sólo muestra pedidos con delivery_fee > 0
            (no cancelados) y permite marcar si el delivery ya se pagó al
            mensajero. Respeta el filtro de fechas de Métricas. NO afecta
            ventas, totales, cierre de caja, facturación ni KDS. */}
        {showCashClose && <DeliveryMsExpressSection restaurantId={restaurant.id} startDate={startDate} endDate={endDate} data-id="element-5369" />}

        {/* Loading State */}
        {analyticsLoading ? <div className="space-y-4" data-id="element-5370">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-5371">
              <Skeleton className="h-28 rounded-xl" data-id="element-5372" />
              <Skeleton className="h-28 rounded-xl" data-id="element-5373" />
              <Skeleton className="h-28 rounded-xl" data-id="element-5374" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5375">
              <Skeleton className="h-36 rounded-xl" data-id="element-5376" />
              <Skeleton className="h-36 rounded-xl" data-id="element-5377" />
              <Skeleton className="h-36 rounded-xl" data-id="element-5378" />
              <Skeleton className="h-36 rounded-xl" data-id="element-5379" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5380">
              <Skeleton className="h-80 rounded-xl" data-id="element-5381" />
              <Skeleton className="h-80 rounded-xl" data-id="element-5382" />
            </div>
          </div> : <>
            {/* Peak Insights & KPIs */}
            <MetricsPeakInsights peakDays={analytics.peakDays} peakHours={analytics.peakHours} channelBreakdown={analytics.channelBreakdown} totalOrders={analytics.totalOrders} totalRevenue={analytics.totalRevenue} averageTicket={analytics.averageTicket} data-id="element-5383" />

            {/* Charts Row 1: Daily Sales + Hourly Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5384">
              <DailySalesChart data={analytics.dailySales} peakDays={analytics.peakDays} data-id="element-5385" />
              <HourlySalesChart data={analytics.hourlySales} peakHours={analytics.peakHours} data-id="element-5386" />
            </div>

            {/* Charts Row 2: Channel Pie + Top Products */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-5387">
              <ChannelPieChart data={analytics.channelBreakdown} data-id="element-5388" />
              <TopProductsChart data={analytics.topProducts} data-id="element-5389" />
            </div>
          </>}

        {/* Orders history (per restaurant, with date filter, excludes cancelled) */}
        <OrdersHistoryTable orders={orders} orderItems={orderItems} restaurantId={restaurant.id} currencySymbol={restaurant.currency_symbol} data-id="element-5390" />

        {/* Profitability Table — collapsible. The chevron in the header toggles the rows. */}
        <Card data-id="element-5391">
          <CardHeader data-id="element-5392">
            <button type="button" onClick={() => setIsProfitabilityOpen(v => !v)} aria-expanded={isProfitabilityOpen} aria-controls="profitability-body" className="w-full flex items-center justify-between gap-3 text-left rounded-lg -m-1 p-1 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-charcoal/40" data-id="element-5393">
              <CardTitle data-id="element-5394">Rentabilidad por Producto</CardTitle>
              <div className="flex items-center gap-2 text-xs text-gray-500 font-medium pr-1" data-id="element-5395">
                <span className="hidden sm:inline" data-id="element-5396">
                  {isProfitabilityOpen ? 'Ocultar' : 'Mostrar'}
                </span>
                {!isProfitabilityOpen && profitability.length > 0 && <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-bold tabular-nums" data-id="element-5397">
                    {profitability.length}
                  </span>}
                <ChevronDown className={isProfitabilityOpen ? 'w-5 h-5 text-gray-400 transition-transform duration-200 rotate-180' : 'w-5 h-5 text-gray-400 transition-transform duration-200'} data-id="element-5398" />
              </div>
            </button>
          </CardHeader>
          {isProfitabilityOpen && <CardContent id="profitability-body" data-id="element-5399">
              {metricsLoading ? <div className="space-y-4" data-id="element-5400">
                  <Skeleton className="h-12 w-full" data-id="element-5401" />
                  <Skeleton className="h-12 w-full" data-id="element-5402" />
                  <Skeleton className="h-12 w-full" data-id="element-5403" />
                  <Skeleton className="h-12 w-full" data-id="element-5404" />
                  <Skeleton className="h-12 w-full" data-id="element-5405" />
                </div> : profitability.length > 0 ? <div className="overflow-x-auto -mx-6" data-id="element-5406">
                  <table className="w-full text-sm text-left min-w-[600px]" data-id="element-5407">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-5408">
                      <tr data-id="element-5409">
                        <th className="px-6 py-3 rounded-l-lg" data-id="element-5410">Producto</th>
                        <th className="px-4 py-3" data-id="element-5411">Categoría</th>
                        <th className="px-4 py-3 text-right" data-id="element-5412">Precio Venta</th>
                        <th className="px-4 py-3 text-right" data-id="element-5413">Costo Est.</th>
                        <th className="px-4 py-3 text-right" data-id="element-5414">Ganancia</th>
                        <th className="px-4 py-3 text-right rounded-r-lg" data-id="element-5415">
                          Margen
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-5416">
                      {profitability.map(item => {
                  const isGoodMargin = item.margin_percent >= 60;
                  const isBadMargin = item.margin_percent < 30;
                  return <tr key={item.menu_item_id} className="border-b border-gray-50 last:border-0" data-id="element-5417">
                            <td className="px-6 py-3 font-medium text-gray-900" data-id="element-5418">
                              {item.item_name}
                            </td>
                            <td className="px-4 py-3 text-gray-500" data-id="element-5419">
                              {item.category || '—'}
                            </td>
                            <td className="px-4 py-3 text-right" data-id="element-5420">
                              {formatCurrency(item.price)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-500" data-id="element-5421">
                              {formatCurrency(item.estimated_cost)}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-emerald-600" data-id="element-5422">
                              {formatCurrency(item.estimated_margin)}
                            </td>
                            <td className="px-4 py-3 text-right" data-id="element-5423">
                              <div className="flex items-center justify-end gap-2" data-id="element-5424">
                                <span className={`font-bold ${isGoodMargin ? 'text-emerald-600' : isBadMargin ? 'text-red-600' : 'text-amber-600'}`} data-id="element-5425">
                                  {Math.round(item.margin_percent)}%
                                </span>
                                {isGoodMargin ? <TrendingUp className="w-4 h-4 text-emerald-500" data-id="element-5426" /> : isBadMargin ? <TrendingDown className="w-4 h-4 text-red-500" data-id="element-5427" /> : null}
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div> : <div className="text-center py-8 text-gray-500 text-sm" data-id="element-5428">
                  No hay datos de rentabilidad disponibles. Asegúrate de
                  configurar los costos en el inventario.
                </div>}
            </CardContent>}
        </Card>
      </div>
    </AdminLayout>;
}